import { Fragment, useMemo, useState } from "react";
import {
  MergeMember,
  MergeField,
  LinkCounts,
  MERGE_FIELDS,
  MERGE_LIST_FIELDS,
  fieldValue,
  combineListValues,
} from "@/lib/memberMerge";
import { normalizePhotoPosition, PHOTO_POSITION_CLASS } from "@/lib/avatar";

interface Props {
  group: MergeMember[];
  selection: Record<string, string>;
  setSelection: (s: Record<string, string>) => void;
  keptId: string;
  setKeptId: (id: string) => void;
  linkCounts: Record<string, LinkCounts>;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

function linkSummary(m: MergeMember, counts: Record<string, LinkCounts>): string {
  const c = counts[String(m.id)] || { regs: 0, banners: 0, waitlist: 0, testimonials: 0 };
  const parts: string[] = [];
  if (c.regs) parts.push(`${c.regs} inscrição${c.regs > 1 ? "ões" : ""} em evento`);
  if (c.banners) parts.push(`${c.banners} anúncio${c.banners > 1 ? "s" : ""}`);
  if (c.waitlist) parts.push(`${c.waitlist} na lista de aviso`);
  if (c.testimonials) parts.push(`${c.testimonials} depoimento${c.testimonials > 1 ? "s" : ""}`);
  return parts.length ? `este cadastro tem ${parts.join(" e ")}` : "este cadastro não tem vínculos";
}

export default function MergeModal({
  group,
  selection,
  setSelection,
  keptId,
  setKeptId,
  linkCounts,
  busy,
  onConfirm,
  onClose,
}: Props) {
  const [step, setStep] = useState<"compare" | "preview">("compare");

  const byId = useMemo(() => new Map(group.map((m) => [String(m.id), m])), [group]);

  function applyAllFrom(memberId: string) {
    const next: Record<string, string> = {};
    MERGE_FIELDS.forEach((f) => {
      next[f.key] = memberId;
    });
    setSelection(next);
  }

  function select(fieldKey: string, memberId: string) {
    const next = { ...selection, [fieldKey]: memberId };
    // A foto e o enquadramento andam juntos: escolher a foto de um cadastro
    // carrega também o enquadramento dele (evita foto cortada de outro lado).
    if (fieldKey === "photo") {
      next["photo_position"] = memberId;
    }
    setSelection(next);
  }

  function renderCellValue(key: string, m: MergeMember) {
    if (key === "photo") {
      return m.photo ? (
        <img
          src={m.photo}
          alt={m.full_name}
          className={`w-14 h-14 rounded-lg object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(m.photo_position)]}`}
        />
      ) : (
        <span className="font-body text-xs text-brown/30 italic">Sem foto</span>
      );
    }
    const v = fieldValue(m, key);
    return v ? (
      <span className="font-body text-sm text-brown break-words block">{v}</span>
    ) : (
      <span className="font-body text-xs text-brown/30 italic">—</span>
    );
  }

  function renderMemberCell(f: MergeField, m: MergeMember) {
    const id = String(m.id);
    const selected = selection[f.key] === id;
    return (
      <button
        key={id}
        onClick={() => select(f.key, id)}
        className={`p-3 text-left border-t border-nude/60 transition-colors cursor-pointer min-h-[44px] ${
          selected ? "bg-primary/5" : "bg-white hover:bg-beige/60"
        }`}
        title={`Usar ${f.label} deste cadastro`}
      >
        <div className="flex items-start gap-2">
          <div
            className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
              selected ? "bg-primary border-primary" : "border-nude"
            }`}
          >
            {selected && <i className="ri-check-line text-white text-[10px]" />}
          </div>
          <div className="min-w-0 flex-1">{renderCellValue(f.key, m)}</div>
        </div>
      </button>
    );
  }

  function renderCombineRow(f: MergeField) {
    const selected = selection[f.key] === "combine";
    const combined = combineListValues(group.map((m) => fieldValue(m, f.key)));
    return (
      <div className="col-span-full bg-beige/60 border-t border-nude/60 px-3 py-2.5">
        <button
          onClick={() => select(f.key, "combine")}
          className="w-full flex items-start gap-2.5 cursor-pointer text-left"
          title="Combinar os valores dos cadastros"
        >
          <div
            className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
              selected ? "bg-primary border-primary" : "border-nude"
            }`}
          >
            {selected && <i className="ri-check-line text-white text-[10px]" />}
          </div>
          <div className="min-w-0 flex-1">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-body text-[10px] uppercase tracking-wide ${
                selected ? "bg-primary text-white" : "bg-primary/10 text-primary"
              }`}
            >
              <i className="ri-git-merge-line" /> Combinar
            </span>
            <p className="font-body text-sm text-brown break-words mt-1">{combined || "—"}</p>
          </div>
        </button>
      </div>
    );
  }

  const preview = MERGE_FIELDS.map((f) => {
    if (MERGE_LIST_FIELDS.has(f.key) && selection[f.key] === "combine") {
      return {
        key: f.key,
        label: f.label,
        icon: f.icon,
        value: combineListValues(group.map((m) => fieldValue(m, f.key))) || "—",
        photo: "",
        position: "top",
      };
    }
    const chosen = byId.get(selection[f.key]);
    if (!chosen) return { key: f.key, label: f.label, icon: f.icon, value: "—", photo: "", position: "top" };
    return {
      key: f.key,
      label: f.label,
      icon: f.icon,
      value: fieldValue(chosen, f.key) || "—",
      photo: f.key === "photo" ? (chosen.photo || "") : "",
      position: f.key === "photo" ? (chosen.photo_position || "top") : "top",
    };
  });

  const keptMember = byId.get(keptId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[92vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-6 py-5 border-b border-nude sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-display text-lg text-brown">
              {step === "compare" ? "Comparar cadastros duplicados" : "Prévia da mesclagem"}
            </h3>
            <p className="font-body text-xs text-brown/50 mt-0.5">
              {step === "compare"
                ? "Clique em cada campo para escolher qual valor permanece no cadastro final."
                : "Confira como o cadastro final vai ficar antes de confirmar."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-brown/40 hover:text-brown hover:bg-beige transition-colors cursor-pointer shrink-0"
            title="Fechar"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === "compare" ? (
            <div
              className="grid gap-px bg-nude/40 rounded-xl overflow-hidden"
              style={{ gridTemplateColumns: `150px repeat(${group.length}, minmax(0, 1fr))` }}
            >
              {/* Corner */}
              <div className="bg-white p-2" />

              {/* Member headers */}
              {group.map((m) => {
                const id = String(m.id);
                const isKept = id === keptId;
                return (
                  <div key={id} className={`bg-white p-3 ${isKept ? "ring-2 ring-primary ring-inset rounded-t-md" : ""}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded-full font-body text-[10px] uppercase tracking-wide whitespace-nowrap ${
                        isKept ? "bg-primary text-white" : "bg-beige text-brown/50"
                      }`}>
                        {isKept ? "Este fica" : "Duplicado"}
                      </span>
                      <button
                        onClick={() => setKeptId(id)}
                        className={`font-body text-[11px] whitespace-nowrap cursor-pointer transition-colors ${
                          isKept ? "text-primary" : "text-brown/40 hover:text-primary"
                        }`}
                        title="Definir este cadastro como o que permanece"
                      >
                        {isKept ? "✓ mantido" : "Manter este"}
                      </button>
                    </div>
                    <p className="font-display text-sm text-brown leading-snug">{m.full_name}</p>
                    <p className="font-body text-[11px] text-brown/40 mt-1 leading-snug">
                      ID {String(m.id)} · {linkSummary(m, linkCounts)}
                    </p>
                    <button
                      onClick={() => applyAllFrom(id)}
                      className="mt-2 w-full px-2 py-1.5 rounded-lg bg-accent-100 text-accent-700 font-body text-[11px] hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
                      title="Selecionar todos os valores deste cadastro"
                    >
                      Usar tudo deste
                    </button>
                  </div>
                );
              })}

              {/* Field rows */}
              {MERGE_FIELDS.map((f) =>
                MERGE_LIST_FIELDS.has(f.key) ? (
                  <Fragment key={f.key}>
                    <div className="contents">
                      <div className="bg-white p-3 flex items-center gap-2 border-t border-nude/60">
                        <div className="w-5 h-5 flex items-center justify-center shrink-0">
                          <i className={`${f.icon} text-xs text-brown/40`} />
                        </div>
                        <span className="font-body text-xs text-brown/60 whitespace-nowrap">{f.label}</span>
                      </div>
                      {group.map((m) => renderMemberCell(f, m))}
                    </div>
                    {renderCombineRow(f)}
                  </Fragment>
                ) : (
                  <div key={f.key} className="contents">
                    <div className="bg-white p-3 flex items-center gap-2 border-t border-nude/60">
                      <div className="w-5 h-5 flex items-center justify-center shrink-0">
                        <i className={`${f.icon} text-xs text-brown/40`} />
                      </div>
                      <span className="font-body text-xs text-brown/60 whitespace-nowrap">{f.label}</span>
                    </div>
                    {group.map((m) => renderMemberCell(f, m))}
                  </div>
                ),
              )}
            </div>
          ) : (
            <div>
              <div className="mb-5 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20">
                <p className="font-body text-sm text-brown">
                  O cadastro <strong className="text-primary">{keptMember?.full_name || "—"}</strong>{" "}
                  (ID {keptId}) permanecerá e receberá os vínculos (inscrições, anúncios, lista de aviso e
                  depoimentos) dos demais. Os duplicados serão marcados como <strong>merged</strong> —
                  nada será apagado.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {preview.map((p) => (
                  <div key={p.label} className="flex items-start gap-2.5 p-3 rounded-xl bg-beige/60">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary/10 shrink-0 mt-0.5">
                      <i className={`${p.icon} text-xs text-primary`} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-body text-[11px] text-brown/40 uppercase tracking-wide mb-0.5">{p.label}</p>
                      {p.key === "photo" && p.photo ? (
                        <img
                          src={p.photo}
                          alt="foto final"
                          className={`w-14 h-14 rounded-lg object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(p.position)]}`}
                        />
                      ) : (
                        <p className="font-body text-sm text-brown break-words">{p.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-nude sticky bottom-0 bg-white">
          {step === "compare" ? (
            <>
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-full font-body text-sm text-brown hover:bg-beige transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => setStep("preview")}
                className="px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Continuar <i className="ri-arrow-right-line ml-1" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep("compare")}
                className="px-5 py-2.5 rounded-full font-body text-sm text-brown hover:bg-beige transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-1" /> Voltar
              </button>
              <button
                onClick={onConfirm}
                disabled={busy}
                className="px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? "Mesclando..." : "Confirmar mesclagem"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}